import { TlfViewModeConfig } from "aic";
import { CommentPanelStatus } from "components/common/comments/interface";
import { EditorBlocksSearchPanelStatus } from "components/tlfpage/TlfEditorBlocksSearchPanel";
import { IUnifiedAction } from "contexts/appsettings/interface";
import { HubGroupMessage } from "contexts/signalr/interface";
import { CommentReadDto } from "functions/api/comments/interface";
import { eHierarchyLevel } from "functions/api/enums";
import { IRequestStatus } from "functions/api/interface";
import { TitlesFootnotesReadDto } from "functions/api/tlfItem/TitlesFootnotesReadDto";
import {
  TlfArsListingReadDto,
  TlfArsTableReadDto,
  TlfItemReadDto,
} from "functions/api/tlfItem/TlfItemReadDto";
import { PatchTlfItemDetails } from "functions/api/tlfItem/patchTlfItem";
import { SaveTlfItemDetails } from "functions/api/tlfItem/saveTlfItem";
import { Operation, Patch } from "rfc6902";
import { TlfContextModalWindow } from "../TlfContextProvider";

export type TlfItemReadDtoContext = TlfItemReadDto | null;
export type TlfRequestStatusType =
  IRequestStatus<PatchTlfItemDetails<keyof TlfItemReadDto>> | IRequestStatus<SaveTlfItemDetails>;

export interface TlfContextProps<T = TlfItemReadDtoContext | TitlesFootnotesReadDto> {
  tlfData: T;
  /** Last persisted TLF data, independent of the undo position. */
  savedTlfData: T;

  // Utility items
  changed: boolean;
  commentPanelStatus: CommentPanelStatus;
  editorBlocksSearchPanelStatus: EditorBlocksSearchPanelStatus;
  forcePushToParent: boolean;
  receivedMessages: TlfUpdateMessage[];
  requestStatus: TlfRequestStatusType;

  // View mode
  tlfViewMode: TlfViewModeConfig;
  variantCreationView: boolean;
  showCreateVariantPatchWindow: boolean;
  appliedVariantPatches: string[];
  activeVariantPatchName: string | null;
  masterTlfData: TlfArsListingReadDto | TlfArsTableReadDto | null;
  activeVariantPatchInvalidOps: Operation[];

  // Traffic lights
  showTrafficLights: boolean;

  // Modal windows
  showModalWindow: TlfContextModalWindow;

  // Subscription Status for Current User
  isSubscribed: boolean;
  readOnly: boolean;

  // Flag to indicate whether the TLF item has been saved to the database or not
  isTlfNew: boolean;

  // Flag to indicate whether there are pending hierarchy updates to be applied in the ARS listing, used to trigger confirmation modals when navigating away from the listing with unsaved changes
  delayedHierarchyUpdates: boolean;
  // ARS deferred update trigger token for delayed hierarchy updates in TLF ARS listing
  applyDelayedHierarchyUpdatesTrigger: number;
  // ARS deferred discard trigger token for delayed hierarchy updates in TLF ARS listing
  discardDelayedHierarchyUpdatesTrigger: number;
}

export interface ITlfContext extends TlfContextProps {
  dispatch: React.Dispatch<IUnifiedAction>;
  patchTlf: <K extends keyof TlfItemReadDto>(
    id: string,
    azsolId: string,
    key: K,
    newValue: TlfItemReadDto[K],
  ) => Promise<void>;
  readOnly: boolean;
  requestStatus: TlfRequestStatusType;
  isSubscribed: boolean;

  /**
   * Saves the TLF Item with the given details.
   * @param azsolId - New AZSOL ID
   * @param version - New Version number
   * @param domain - New Domain
   * @param primaryHierarchyId - Hierarchy to save to
   * @param additionalInformation - New Additional information
   * @param copiedFromHierarchyLevel - Original object level
   * @param tocNumber - New Toc Number
   * @param sapReference - New SAP Reference
   * @returns Promise that resolves when the save is complete.
   */
  saveTlf: (
    azsolId?: string,
    version?: number,
    domain?: string,
    primaryHierarchyId?: string,
    additionalInformation?: string,
    copiedFromHierarchyLevel?: eHierarchyLevel,
    tocNumber?: string,
    sapReference?: string,
  ) => Promise<void>;
}

export interface TlfUpdateMessage {
  messageId: string;
  updatedId: string;
  updateTS: Date;
}

export const ACTIVATE_VARIANT_PATCH = "ACTIVATE_VARIANT_PATCH";
export const ADD_RECEIVED_MESSAGE = "ADD_RECEIVED_MESSAGE";
export const APPLY_ARS_UPDATES = "APPLY_ARS_UPDATES";
export const CANCEL_VARIANT_PATCH_CREATION = "CANCEL_VARIANT_PATCH_CREATION";
export const CLEAR_TLF = "CLEAR_TLF";
export const CLOSE_MODAL_WINDOW = "CLOSE_MODAL_WINDOW";
export const CREATE_VARIANT_PATCH = "CREATE_VARIANT_PATCH";
export const DISCARD_ARS_UPDATES = "DISCARD_ARS_UPDATES";
export const LOAD_TLF = "LOAD_TLF";
export const PATCH_TLF_COMMENTS = "PATCH_TLF_COMMENTS";
export const PROCESS_SIGNALR_NOTIFICATION = "PROCESS_SIGNALR_NOTIFICATION";
export const REMOVE_TLF_COMMENT = "REMOVE_TLF_COMMENT";
export const REMOVE_VARIANT_PATCH = "REMOVE_VARIANT_PATCH";
export const RENAME_VARIANT_PATCH = "RENAME_VARIANT_PATCH";
export const REVERT_TO_VARIANT_MASTER = "REVERT_TO_VARIANT_MASTER";
export const SET_CREATE_VARIANT_PATCH_WINDOW = "SET_CREATE_VARIANT_PATCH_WINDOW";
export const SET_DELAYED_HIERARCHY_UPDATES = "SET_DELAYED_HIERARCHY_UPDATES";
export const SET_EDITOR_BLOCKS_SEARCH_PANEL = "SET_EDITOR_BLOCKS_SEARCH_PANEL";
export const SET_TLF_COMMENT_PANEL = "SET_TLF_COMMENT_PANEL";
export const SET_TLF_READ_ONLY = "SET_TLF_READ_ONLY";
export const SET_TLF_REQUEST_STATUS = "SET_TLF_REQUEST_STATUS";
export const SET_TLF_SUBSCRIPTION_STATUS = "SET_TLF_SUBSCRIPTION_STATUS";
export const SET_VARIANT_CREATION_VIEW = "SET_VARIANT_CREATION_VIEW";
export const SET_VARIANT_PATCH_SELECTION = "SET_VARIANT_PATCH_SELECTION";
export const SET_VIEW_MODE = "SET_VIEW_MODE";
export const SHOW_MODAL_WINDOW = "SHOW_MODAL_WINDOW";
export const TOGGLE_TRAFFIC_LIGHTS = "TOGGLE_TRAFFIC_LIGHTS";
export const TOGGLE_VARIANT_PATCH = "TOGGLE_VARIANT_PATCH";
export const TRACK_ACTIVE_VARIANT_PATCH = "TRACK_ACTIVE_VARIANT_PATCH";
export const UPDATE_TLF = "UPDATE_TLF";
export const UPDATE_TLF_COMMENT = "UPDATE_TLF_COMMENT";

export type TlfOperation =
  | typeof ACTIVATE_VARIANT_PATCH
  | typeof ADD_RECEIVED_MESSAGE
  | typeof APPLY_ARS_UPDATES
  | typeof CANCEL_VARIANT_PATCH_CREATION
  | typeof CLEAR_TLF
  | typeof CLOSE_MODAL_WINDOW
  | typeof CREATE_VARIANT_PATCH
  | typeof DISCARD_ARS_UPDATES
  | typeof LOAD_TLF
  | typeof PATCH_TLF_COMMENTS
  | typeof PROCESS_SIGNALR_NOTIFICATION
  | typeof REMOVE_TLF_COMMENT
  | typeof REMOVE_VARIANT_PATCH
  | typeof RENAME_VARIANT_PATCH
  | typeof REVERT_TO_VARIANT_MASTER
  | typeof SET_CREATE_VARIANT_PATCH_WINDOW
  | typeof SET_DELAYED_HIERARCHY_UPDATES
  | typeof SET_EDITOR_BLOCKS_SEARCH_PANEL
  | typeof SET_TLF_COMMENT_PANEL
  | typeof SET_TLF_READ_ONLY
  | typeof SET_TLF_REQUEST_STATUS
  | typeof SET_TLF_SUBSCRIPTION_STATUS
  | typeof SET_VARIANT_CREATION_VIEW
  | typeof SET_VARIANT_PATCH_SELECTION
  | typeof SET_VIEW_MODE
  | typeof SHOW_MODAL_WINDOW
  | typeof TOGGLE_TRAFFIC_LIGHTS
  | typeof TOGGLE_VARIANT_PATCH
  | typeof TRACK_ACTIVE_VARIANT_PATCH
  | typeof UPDATE_TLF
  | typeof UPDATE_TLF_COMMENT;

export interface ITlfActionBase {
  operation: TlfOperation;
  calledFrom: string;
}

export interface ITlfActionActivateVariantPatch extends ITlfActionBase {
  operation: typeof ACTIVATE_VARIANT_PATCH;
  patchName: string;
}

export interface ITlfActionAddReceivedMessage extends ITlfActionBase {
  operation: typeof ADD_RECEIVED_MESSAGE;
  receivedMessage: HubGroupMessage;
}

export interface ITlfActionApplyArsUpdates extends ITlfActionBase {
  operation: typeof APPLY_ARS_UPDATES;
}

export interface ITlfActionCancelVariantPatchCreation extends ITlfActionBase {
  operation: typeof CANCEL_VARIANT_PATCH_CREATION;
}

export interface ITlfActionClearTlf extends ITlfActionBase {
  operation: typeof CLEAR_TLF;
}

export interface ITlfActionCloseModalWindow extends ITlfActionBase {
  operation: typeof CLOSE_MODAL_WINDOW;
}

export interface ITlfActionCreateVariantPatch extends ITlfActionBase {
  operation: typeof CREATE_VARIANT_PATCH;
  patchName: string;
}

export interface ITlfActionDiscardArsUpdates extends ITlfActionBase {
  operation: typeof DISCARD_ARS_UPDATES;
}

export interface ITlfActionLoadTlf extends ITlfActionBase {
  operation: typeof LOAD_TLF;
  tlfData: TlfItemReadDto | TitlesFootnotesReadDto;
  isSubscribed: boolean;
  isTlfNew: boolean;
  readOnly: boolean;
}

export interface ITlfActionPatchTlfComments extends ITlfActionBase {
  operation: typeof PATCH_TLF_COMMENTS;
  patch: Patch;
}

export interface ITlfActionProcessSignalRNotification extends ITlfActionBase {
  operation: typeof PROCESS_SIGNALR_NOTIFICATION;
  patch: Operation[];
}

export interface ITlfActionRemoveTlfComment extends ITlfActionBase {
  operation: typeof REMOVE_TLF_COMMENT;
  commentId: string;
}

/**
 * Removes a named patch from the catalogue and its selections.
 */
export interface ITlfActionRemoveVariantPatch extends ITlfActionBase {
  operation: typeof REMOVE_VARIANT_PATCH;
  patchName: string;
}

/**
 * Renames a patch while retaining its operations and template references.
 */
export interface ITlfActionRenameVariantPatch extends ITlfActionBase {
  operation: typeof RENAME_VARIANT_PATCH;
  patchName: string;
  newPatchName: string;
}

export interface ITlfActionRevertToVariantMaster extends ITlfActionBase {
  operation: typeof REVERT_TO_VARIANT_MASTER;
}

export interface ITlfActionSetCreateVariantPatchWindow extends ITlfActionBase {
  operation: typeof SET_CREATE_VARIANT_PATCH_WINDOW;
  show: boolean;
}

export interface ITlfActionSetDelayedHierarchyUpdates extends ITlfActionBase {
  operation: typeof SET_DELAYED_HIERARCHY_UPDATES;
  delayedHierarchyUpdates: boolean;
}

export interface ITlfActionSetEditorBlocksSearchPanel extends ITlfActionBase {
  operation: typeof SET_EDITOR_BLOCKS_SEARCH_PANEL;
  editorBlocksSearchPanelStatus: EditorBlocksSearchPanelStatus;
}

export interface ITlfActionSetReadOnly extends ITlfActionBase {
  operation: typeof SET_TLF_READ_ONLY;
  readOnly: boolean;
}

export interface ITlfActionSetRequestStatus extends ITlfActionBase {
  operation: typeof SET_TLF_REQUEST_STATUS;
  requestStatus: TlfRequestStatusType;
}

export interface ITlfActionSetSubscriptionStatus extends ITlfActionBase {
  operation: typeof SET_TLF_SUBSCRIPTION_STATUS;
  isSubscribed: boolean;
}

export interface ITlfActionSetTlfCommentPanel extends ITlfActionBase {
  operation: typeof SET_TLF_COMMENT_PANEL;
  commentPanelStatus: CommentPanelStatus;
}

export interface ITlfActionSetVariantCreationView extends ITlfActionBase {
  operation: typeof SET_VARIANT_CREATION_VIEW;
  variantCreationView: boolean;
}

export interface ITlfActionSetVariantPatchSelection extends ITlfActionBase {
  operation: typeof SET_VARIANT_PATCH_SELECTION;
  appliedPatchNames: string[];
  activePatchName: string;
}

export interface ITlfActionSetViewMode extends ITlfActionBase {
  operation: typeof SET_VIEW_MODE;
  tlfViewMode: TlfViewModeConfig;
}

export interface ITlfActionShowModalWindow extends ITlfActionBase {
  operation: typeof SHOW_MODAL_WINDOW;
  modalWindow: TlfContextModalWindow;
}

export interface ITlfActionToggleTrafficLights extends ITlfActionBase {
  operation: typeof TOGGLE_TRAFFIC_LIGHTS;
  showTrafficLights: boolean;
}

export interface ITlfActionToggleVariantPatch extends ITlfActionBase {
  operation: typeof TOGGLE_VARIANT_PATCH;
  patchName: string;
  applied: boolean;
}

export interface ITlfActionTrackActiveVariantPatch extends ITlfActionBase {
  operation: typeof TRACK_ACTIVE_VARIANT_PATCH;
}

export interface ITlfActionUpdateTlf extends ITlfActionBase {
  operation: typeof UPDATE_TLF;
  tlfData: Partial<TlfItemReadDto>;
  doNotAddUndo?: boolean;
  newSavePoint?: boolean;
  reset?: boolean;
}

export interface ITlfActionUpdateTlfComment extends ITlfActionBase {
  operation: typeof UPDATE_TLF_COMMENT;
  comment: CommentReadDto;
}

export type ITlfAction =
  | ITlfActionActivateVariantPatch
  | ITlfActionAddReceivedMessage
  | ITlfActionApplyArsUpdates
  | ITlfActionCancelVariantPatchCreation
  | ITlfActionClearTlf
  | ITlfActionCloseModalWindow
  | ITlfActionCreateVariantPatch
  | ITlfActionDiscardArsUpdates
  | ITlfActionLoadTlf
  | ITlfActionPatchTlfComments
  | ITlfActionProcessSignalRNotification
  | ITlfActionRemoveTlfComment
  | ITlfActionRemoveVariantPatch
  | ITlfActionRenameVariantPatch
  | ITlfActionRevertToVariantMaster
  | ITlfActionSetCreateVariantPatchWindow
  | ITlfActionSetDelayedHierarchyUpdates
  | ITlfActionSetEditorBlocksSearchPanel
  | ITlfActionSetReadOnly
  | ITlfActionSetRequestStatus
  | ITlfActionSetSubscriptionStatus
  | ITlfActionSetTlfCommentPanel
  | ITlfActionSetVariantCreationView
  | ITlfActionSetVariantPatchSelection
  | ITlfActionSetViewMode
  | ITlfActionShowModalWindow
  | ITlfActionToggleTrafficLights
  | ITlfActionToggleVariantPatch
  | ITlfActionTrackActiveVariantPatch
  | ITlfActionUpdateTlf
  | ITlfActionUpdateTlfComment;
